import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { Button, Checkbox, Space, Modal } from '@bedrock/components';
import { ChevronRight, CheckHeavy } from '@bedrock/icons-react';
import { useControllableValue, useInterval, useInViewport, useMemoizedFn, useUpdate } from 'ahooks';
import cx from 'classnames';
import './index.less';

export interface AgreeCheckBoxProps {
  /** checkbox 的值 */
  value?: boolean;
  /** checkbox 值改变时的回调 */
  onChange?: (value: boolean) => void;
  /** checkbox 的文字 */
  checkboxText?: string;
  /** 弹框按钮的文字 */
  buttonText?: string;
  /** 弹框标题 */
  modalTitle?: string;
  /** 弹框内容 */
  modalContent?: React.ReactNode;
  /** 弹框确认按钮文字 */
  okButtonText?: string;
  /** 弹框取消按钮文字 */
  cancelButtonText?: string;
  /** 弹框宽度 */
  modalWidth?: number | string;
  /** 是否禁用整个组件 */
  disabled?: boolean;
  /** 弹框内容滚动到底部后是否自动启用确认按钮 */
  autoEnableOnScroll?: boolean;
  /** 弹框内容滚动到底部后的回调 */
  onScrollToBottom?: () => void;
}

export interface AgreeCheckBoxRef {
  /** 打开弹框 */
  openModal: () => void;
  /** 关闭弹框 */
  closeModal: () => void;
  /** 获取弹框是否可见 */
  isModalVisible: () => boolean;
}

const AgreeCheckBox = forwardRef<AgreeCheckBoxRef, AgreeCheckBoxProps>(({
  value = false,
  onChange,
  checkboxText = '我已阅读并同意',
  buttonText = '查看详情',
  modalTitle = '请仔细阅读',
  modalContent,
  okButtonText = '我已阅读并同意',
  cancelButtonText = '取消',
  modalWidth = 800,
  disabled = false,
  autoEnableOnScroll = true,
  onScrollToBottom,
  ...restProps
}, ref) => {
  const [checked, setChecked] = useControllableValue({ value, onChange });
  const [modalVisible, setModalVisible] = useState(false);
  const [confirmDisabled, setConfirmDisabled] = useState(true);
  const contentRef = useRef<HTMLDivElement>(null);
  const parentRef = useRef<HTMLDivElement>(null);
  const pendingResolveRef = useRef<((value: boolean) => void) | null>(null);
  const footerRef = useRef<HTMLDivElement>(null);

  // 监听滚动到底部
  const [inViewport] = useInViewport(() => footerRef.current);

  const getChecked = useMemoizedFn(() => {
    if(checked) {
      return Promise.resolve(true)
    } else {
      return new Promise((resolve) => {
          // 保存 resolve 函数到 ref 中
          pendingResolveRef.current = resolve;
          
          // 打开弹框
          setModalVisible(true);
          setConfirmDisabled(true);
      });
    }
  });

  // 暴露方法给外部
  useImperativeHandle(ref, () => ({
    getChecked,
    openModal: () => {
      if (!disabled) {
        setModalVisible(true);
        setConfirmDisabled(true);
      }
    },
    closeModal: () => {
      setModalVisible(false);
      setConfirmDisabled(true);
    },
    isModalVisible: () => modalVisible,
  }), [disabled, modalVisible]);

  const update = useUpdate();
  useInterval(() => {
    if (modalVisible) {
      update();
    } 
  }, 400)

  useEffect(() => {
    if (modalVisible) {
      if (inViewport && autoEnableOnScroll) {
        setConfirmDisabled(false);
      }
    }
  }, [modalVisible, inViewport, autoEnableOnScroll]);

  useEffect(() => {
    if (modalVisible && inViewport) {
      onScrollToBottom?.();
    } 
  }, [modalVisible, inViewport, onScrollToBottom]);



  const handleCheckboxChange = (checked: boolean) => {
    if (!disabled) {
      // 如果已经选中，改变状态为false，否则改变状态为打开弹框
      setChecked((oldCheck) => {
        if(oldCheck) {
          return false
        } else {
          handleOpenModal()
        }
      });
    }
  };

  const handleCheckboxClick = (e: React.MouseEvent) => {
    // 如果checkbox未选中，点击时打开弹框
    e.preventDefault(); // 阻止默认的checkbox状态改变
    if (!checked && !disabled) {
      handleOpenModal();
    }
  };

  const handleOpenModal = () => {
    if (!disabled) {
      setModalVisible(true);
      setConfirmDisabled(true);
    }
  };

  const handleModalOk = () => {
    setModalVisible(false);
    setChecked(true);
    
    // 如果有待处理的 Promise，resolve 它
    if (pendingResolveRef.current) {
      pendingResolveRef.current(true);
      pendingResolveRef.current = null;
    }
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    
    // 如果有待处理的 Promise，resolve 它
    if (pendingResolveRef.current) {
      pendingResolveRef.current(false);
      pendingResolveRef.current = null;
    }
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setConfirmDisabled(true);
    
    // 如果有待处理的 Promise，resolve 它
    if (pendingResolveRef.current) {
      pendingResolveRef.current(false);
      pendingResolveRef.current = null;
    }
  };

  return (
    <div className={cx('agree-checkbox')}>
      
        <Checkbox
          checked={checked}
          disabled={disabled}
          onChange={(e) => handleCheckboxChange(e.target.checked)}
          onClick={handleCheckboxClick}
          {...restProps}
        >
            <span className={cx('agree-checkbox-text')}>{checkboxText}</span>
            <Button
                type="link"
                onClick={handleOpenModal}
                disabled={disabled}
                >
                {buttonText}
            </Button>
        </Checkbox>
        
      

      <Modal
        title={modalTitle}
        visible={modalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        onClose={handleModalClose}
        width={Number(modalWidth)}
        okButtonProps={{
          disabled: confirmDisabled,
          icon: !confirmDisabled ? <CheckHeavy /> : undefined,
        }}
        okText={confirmDisabled ? '请滚动到底部阅读完整内容' : okButtonText}
        cancelText={cancelButtonText}
        cancelButton={!!cancelButtonText}
        wrapClassName={cx('agree-modal-wrap')}
        destroyOnClose
      >
        <div
          ref={parentRef}
          id="agree-modal-parent"
          className={cx('agree-modal-parent')}
        >
          <div
            ref={contentRef}
            className={cx('agree-modal-content')}
          >
            {modalContent}
          </div>
          <div
            className={cx('agree-modal-footer')}
            id="agree-modal-footer"
            ref={footerRef}
          />
        </div>
      </Modal>
    </div>
  );
});

AgreeCheckBox.displayName = 'AgreeCheckBox';

export default AgreeCheckBox;

